import { api } from './client'

export interface PushSubscriptionPayload {
  endpoint: string
  p256dh: string
  auth: string
  user_agent?: string
}

export interface PushDevice {
  id: number
  user_agent: string | null
  created_at: string
}

export const pushSubscriptionsApi = {
  getVapidPublicKey: () =>
    api.get<{ public_key: string }>('/v1/admin/push-public-key').then((r) => r.data.public_key),
  list: () =>
    api.get<PushDevice[]>('/v1/me/push-subscriptions').then((r) => r.data),
  register: (payload: PushSubscriptionPayload) =>
    api.post<PushDevice>('/v1/me/push-subscriptions', payload).then((r) => r.data),
  remove: (id: number) =>
    api.delete(`/v1/me/push-subscriptions/${id}`).then((r) => r.data),
  sendTest: () =>
    api.post<{ sent: number }>('/v1/me/push-test').then((r) => r.data),
}

/** Helper pra converter VAPID public key (URL-safe base64) → Uint8Array */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const arr = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i)
  return arr
}

/** Garantia de boot: se o usuário já concedeu permissão mas a subscription
 *  sumiu (ex.: storage limpo, browser update), resubscreve silenciosamente.
 *  Idempotente — roda no máximo uma vez por carregamento de página. */
let _healCheckedThisBoot = false

export async function ensurePushHealthy(): Promise<void> {
  if (_healCheckedThisBoot) return
  _healCheckedThisBoot = true
  try {
    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      typeof Notification === 'undefined' ||
      Notification.permission !== 'granted'
    ) return
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (!sub) {
      await subscribeToPush()
    }
  } catch {
    /* best-effort — nunca lança */
  }
}

/** Registra service worker + pede permissao + cria subscription */
export async function subscribeToPush(): Promise<PushSubscriptionPayload | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return null

  const reg = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready

  const publicKey = await pushSubscriptionsApi.getVapidPublicKey()
  // applicationServerKey aceita BufferSource. Cast via .buffer evita
  // erro TS2322 com Uint8Array<ArrayBufferLike> em strict mode.
  const keyBytes = urlBase64ToUint8Array(publicKey)
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes.buffer as ArrayBuffer,
  })

  const json = sub.toJSON()
  const payload: PushSubscriptionPayload = {
    endpoint: json.endpoint!,
    p256dh: json.keys!.p256dh,
    auth: json.keys!.auth,
    user_agent: navigator.userAgent,
  }
  await pushSubscriptionsApi.register(payload)
  return payload
}
